/*------------------------------------------------------------------------------
   About      : Validate button + result tiles (epic DA-697, II-FE-6 / DA-2393)

                Mockup: `image-2.png` — the VARIABLES / SEMANTIC / CAPPING tiles
                under the template field.

                Two DIFFERENT checks sit behind one button, and conflating them
                would be wrong:

                **Variables** — `POST /v1/insights/validate-template-text`,
                which relays quill's `IIValidationUtils.validateTemplateString`
                against the live DerivedQuantity registry. Authoritative and
                mechanical. This is the same rule set `createInsight` /
                `updateAll` enforce on write, so a failure here WILL fail a save.

                **Semantic** — `POST /v1/insights/semantic-validate`, an LLM
                judgement on whether the sentence means what its classification
                says. **Advisory only — it never gates a save**, and the UI must
                not imply otherwise. Mechanical validation (DA-2368) remains the
                only content gate.

                **Capping** is neither, and there is no endpoint for it. What it
                checks is the one capping precondition this screen owns: every
                variable constrained in `templateCriteria` must also appear as a
                token in the template text, or the value is capped for a
                sentence that never renders it. That is computed locally from
                the two fields, so unlike the other two it needs no Validate
                click and updates as the author types.

   Copyright (c) 2026 Bidgely. All rights reserved.
------------------------------------------------------------------------------*/
import { ReactElement, useEffect, useMemo, useState } from 'react'
import { Alert, Box, Button, CircularProgress, Paper, Stack, Tooltip,
         Typography, alpha } from '@mui/material'
import CheckCircleIcon   from '@mui/icons-material/CheckCircleOutline'
import ErrorIcon         from '@mui/icons-material/ErrorOutline'
import WarningIcon       from '@mui/icons-material/WarningAmber'
import HelpOutlineIcon   from '@mui/icons-material/HelpOutline'
import FactCheckIcon     from '@mui/icons-material/FactCheckOutlined'

import { useGlobalConfig }  from 'context/GlobalContext'
import { USER_ROLE }        from 'framework/session/PermissionsManager'
import { ANALYTICS_EVENTS } from 'framework/analytics/AnalyticsEvents'

import { InsightApis }              from '../api/insightAPI'
import { InsightCatalog,
         InsightCreateData,
         InsightValidationState,
         SEMANTIC_CLASSIFICATION_FIELDS,
         SemanticValidateResponse,
         TemplateTextValidation }   from '../types/insightTypes'
import { criteriaVariablesMissingFromText,
         definitionFieldGaps,
         displayNameFor }           from '../utils/insightCatalogHelpers'
import { buildValidationState,
         deriveCappingCheck,
         deriveInsightValidation,
         deriveVariablesCheck }     from '../utils/insightValidation'
import type { BuiltValidationState } from '../utils/insightValidation'
import { SUB_HEADING_SX }           from './insightFormStyles'

type TileState = 'UNKNOWN' | 'PASS' | 'WARN' | 'FAIL' | 'UNAVAILABLE'

/** What the save flow needs to know about validation. */
export interface ValidationGate {
  /** The template text changed, so the recorded verdict no longer applies. */
  requiresValidation : boolean
  /** All required checks are green for exactly this text. */
  satisfied          : boolean
  /** Green but for the semantic check, which only a TPM may waive. */
  semanticIsTheBlocker : boolean
  /** The object to persist — client-owned, stored verbatim, replaced wholesale. */
  built              : BuiltValidationState | undefined
}

interface TemplateValidationPanelProps {
  templateString    : string
  /**
   * The text as stored. `undefined` in create mode, where there is nothing to
   * compare against and a validation is always warranted.
   */
  originalTemplateString ?: string
  /** Reports the gate upward so the form can block a save on it. */
  onGateChange      ?: (gate: ValidationGate) => void
  /**
   * What is stored on the record. Read back so a verdict persisted on a previous
   * visit renders instead of showing "not run" — the slots describe the stored
   * text, so they apply exactly while the author has not edited it.
   */
  storedValidationState ?: InsightValidationState | null
  /** Used to build the classification the semantic check reasons about, and —
   *  via `templateCriteria` — to run the capping precondition check. */
  insight           : InsightCreateData
  catalog          ?: InsightCatalog
  pilotId          ?: string
  insightDocumentId?: string
}

/*------------------------------------------------------------------------------
   Tile
------------------------------------------------------------------------------*/

const TILE_ICON: Record<TileState, ReactElement> = {
  PASS        : <CheckCircleIcon fontSize="small" color="success" />,
  WARN        : <WarningIcon fontSize="small" color="warning" />,
  FAIL        : <ErrorIcon fontSize="small" color="error" />,
  UNKNOWN     : <HelpOutlineIcon fontSize="small" color="disabled" />,
  UNAVAILABLE : <HelpOutlineIcon fontSize="small" color="disabled" />,
}

/**
 * The palette key each state tints the whole tile with.
 *
 * Derived from `palette.<key>.main` at render time rather than named as
 * `success.50` / `error.50`: this app's theme defines only main/light/dark per
 * severity, so a numeric shade resolves to nothing and the tile renders with no
 * fill at all — which is what made a failing check look identical to a passing
 * one.
 */
const TILE_TONE: Record<TileState, 'success' | 'warning' | 'error' | null> = {
  PASS        : 'success',
  WARN        : 'warning',
  FAIL        : 'error',
  UNKNOWN     : null,
  UNAVAILABLE : null,
}

const ValidationTile = ({
  label, state, caption, tooltip,
}: { label: string; state: TileState; caption: string; tooltip?: string }) => {

  const tone = TILE_TONE[state]

  return (
    <Tooltip title={tooltip ?? ''} placement="top" arrow>
      <Paper
        variant="outlined"
        sx={{
          flex      : 1,
          p         : 1.5,
          textAlign : 'center',
          opacity   : state === 'UNAVAILABLE' ? 0.6 : 1,
          // The whole box carries the verdict — fill, border and text — so the
          // result is readable at a glance without parsing the icon.
          ...(tone ? {
            bgcolor     : (theme) => alpha(theme.palette[tone].main, theme.palette.mode === 'dark' ? 0.18 : 0.1),
            borderColor : (theme) => alpha(theme.palette[tone].main, 0.5),
          } : undefined),
        }}
      >
        <Box sx={{ display : 'flex', justifyContent : 'center', mb : 0.5 }}>{TILE_ICON[state]}</Box>
        <Typography
          variant="caption"
          sx={{
            ...SUB_HEADING_SX,
            ...(tone ? { color : `${tone}.main` } : undefined),
          }}
        >
          {label}
        </Typography>
        <Typography variant="caption" color={tone ? `${tone}.main` : 'text.secondary'}>
          {caption}
        </Typography>
      </Paper>
    </Tooltip>
  )
}

/*------------------------------------------------------------------------------
   Panel
------------------------------------------------------------------------------*/

export const TemplateValidationPanel = ({
  templateString,
  originalTemplateString,
  onGateChange,
  storedValidationState,
  insight,
  catalog,
  pilotId,
  insightDocumentId,
}: TemplateValidationPanelProps) => {

  const { rc } = useGlobalConfig()

  /* Only a TPM may save past a semantic verdict that is not green. */
  const canWaiveSemantic = rc.permissionsManager.getUserRole() === USER_ROLE.TPM

  const [isRunning, setIsRunning] = useState(false)
  const [tokens, setTokens]       = useState<TemplateTextValidation | undefined>()
  /** The exact text `tokens` describes, so a stale verdict can be detected. */
  const [validatedText, setValidatedText] = useState<string | undefined>()
  const [semantic, setSemantic]   = useState<SemanticValidateResponse | undefined>()
  const [tokenError, setTokenError]       = useState<string | undefined>()
  const [semanticError, setSemanticError] = useState<string | undefined>()

  const runValidation = async () => {
    setIsRunning(true)
    setTokenError(undefined)
    setSemanticError(undefined)
    setTokens(undefined)
    setSemantic(undefined)

    rc.analytics.trackEvent(ANALYTICS_EVENTS.NBI_INSIGHTS.VALIDATE_REQUESTED)

    // Only the fields proxypulse actually forwards to the model — sending the
    // whole record would put arbitrary keys in a prompt for no benefit.
    const classification: Record<string, unknown> = {}
    for (const field of SEMANTIC_CLASSIFICATION_FIELDS) {
      const value = (insight as Record<string, unknown>)[field]
      if (value !== undefined && value !== null) classification[field] = value
    }

    // Run together: they are independent, and the LLM call is much the slower
    // of the two — serialising would make the mechanical result wait on it.
    const [tokenResult, semanticResult] = await Promise.all([
      InsightApis.validateTemplateText(rc, { templateString, type : insight.type }),
      InsightApis.semanticValidate(rc, {
        templateString,
        classification,
        pilotId,
        insightDocumentId,
      }),
    ])

    if (tokenResult.error || !tokenResult.data) {
      setTokenError(tokenResult.error?.message || 'Token validation could not run.')
    } else {
      setTokens(tokenResult.data)
      // Captured from the closure, not read fresh: this is the text the server
      // actually judged, even if the author has typed on since.
      setValidatedText(templateString)
    }

    // A semantic failure is not a content failure — surfaced separately so an
    // unavailable model never reads as "this insight is broken".
    if (semanticResult.error || !semanticResult.data) {
      setSemanticError(semanticResult.error?.message || 'Semantic check could not run.')
    } else {
      setSemantic(semanticResult.data)
    }

    setIsRunning(false)
  }

  /* ── VARIABLES ───────────────────────────────────────────────────────────
     Derived live from the catalog, exactly as the list column does, so the tile
     tracks what the author is typing.

     The server verdict overrides it, but ONLY while it still describes the
     current text. Previously the result simply persisted: edit a failing
     sentence into a valid one and the tile stayed red until Validate was
     clicked again — the stale answer looked like a live one. Anchoring it to
     the exact string it was computed for makes going stale impossible. */
  const derivedVariables = useMemo(
    () => deriveVariablesCheck({
      templateString,
      templateCriteria : insight.templateCriteria,
      pickUnitType     : insight.pickUnitType,
      pickUnitCount    : insight.pickUnitCount,
      compareUnitType  : insight.compareUnitType,
      compareUnitCount : insight.compareUnitCount,
    }, catalog),
    [templateString, insight.templateCriteria, insight.pickUnitType, insight.pickUnitCount,
     insight.compareUnitType, insight.compareUnitCount, catalog],
  )

  /* Variables that read a record field the record leaves unset.
     The server CANNOT check this: `validate-template-text` is stateless and
     never receives the record, so it reports the dependency on each placeholder
     and skips the check. `createInsight` / `updateAll` do enforce it, so without
     this the panel would show a green tile and an enabled Save, and the save
     would then fail for a reason nothing on screen predicted. */
  const definitionGaps = useMemo(
    () => definitionFieldGaps({
      templateString,
      templateCriteria : insight.templateCriteria,
      pickUnitType     : insight.pickUnitType,
      pickUnitCount    : insight.pickUnitCount,
      compareUnitType  : insight.compareUnitType,
      compareUnitCount : insight.compareUnitCount,
    }, catalog),
    [templateString, insight.templateCriteria, insight.pickUnitType, insight.pickUnitCount,
     insight.compareUnitType, insight.compareUnitCount, catalog],
  )

  const serverVerdictIsCurrent = tokens !== undefined && validatedText === templateString

  /* A slot stored on the record describes the STORED text, so it applies only
     while the author has not edited it. Once they have, it is stale and the
     live derivation takes over. */
  const storedApplies = originalTemplateString !== undefined
                     && templateString === originalTemplateString

  const storedSummary = useMemo(
    () => deriveInsightValidation(
      { templateString, templateCriteria : insight.templateCriteria,
        validationState : storedApplies ? storedValidationState : undefined },
      catalog,
    ),
    [templateString, insight.templateCriteria, storedValidationState, storedApplies, catalog],
  )

  /* A definition gap fails the tile even when the server said valid — the server
     was not shown the record, so its "valid" is scoped to the text alone. */
  const variablesState : TileState = definitionGaps.length ? 'FAIL'
    : serverVerdictIsCurrent
      ? (tokens!.valid ? 'PASS' : 'FAIL')
      : storedSummary.variables.verdict

  const variablesCaption = definitionGaps.length
    ? `${definitionGaps.length} field${definitionGaps.length === 1 ? '' : 's'} not set`
    : serverVerdictIsCurrent
      ? (tokens!.valid
          ? 'Matched'
          : `${tokens!.errors.length} problem${tokens!.errors.length === 1 ? '' : 's'}`)
      : storedSummary.variables.verdict === 'UNKNOWN'
        ? 'Not run'
        : storedSummary.variables.verdict === 'PASS' ? 'Matched' : 'Has a problem'

  /* In-session result wins; otherwise fall back to what was stored, so a
     verdict persisted on an earlier visit is not reported as "not run". */
  const storedSemantic = storedSummary.semantic
  const semanticState : TileState = semanticError ? 'UNKNOWN'
    : semantic ? semantic.verdict
    : storedSemantic.verdict

  /* ── CAPPING ─────────────────────────────────────────────────────────────
     Local and live — the author sees it change the moment they add a condition
     for a variable the sentence does not mention. Shares the list's helper so
     the tile and the row's dot can never disagree. */
  const derivedCapping = useMemo(
    () => deriveCappingCheck({ templateCriteria : insight.templateCriteria, templateString }, catalog),
    [insight.templateCriteria, templateString, catalog],
  )
  const cappingState : TileState = derivedCapping.verdict

  /* ── The save gate ───────────────────────────────────────────────────────
     Validating is warranted exactly when the template text differs from what
     is stored — on create there is nothing stored, so it is always warranted.
     An untouched sentence has already been validated by quill (it stamps
     `variables: PASS` on every successful write), so re-running it would be
     asking the author to re-certify text nobody changed.

     Only the MECHANICAL result satisfies the gate. Semantic is advisory by
     design and must never block a save: it is a model's opinion, quill does not
     enforce it, and an unavailable LLM (a 503) would otherwise lock an author
     out of saving legitimate content entirely. It is surfaced loudly instead. */
  const textChanged = originalTemplateString === undefined
    ? !!templateString.trim()
    : templateString !== originalTemplateString

  /* All three must be green to save — except that a TPM may waive the semantic
     check. The waiver exists because semantic is an LLM opinion: without it, an
     unavailable model (a 503) would lock every author out of saving legitimate
     content. Mechanical checks are never waivable; quill would reject those on
     write regardless of who is asking. */
  // A definition gap blocks regardless of the server verdict: quill runs the
  // per-record check on write, so saving would be rejected anyway.
  const variablesGreen = definitionGaps.length === 0
    && (serverVerdictIsCurrent ? tokens!.valid : false)
  const cappingGreen   = derivedCapping.verdict === 'PASS'
  const semanticGreen  = semantic
    ? semantic.verdict === 'PASS'
    : storedSemantic.verdict === 'PASS'

  const mechanicalGreen = variablesGreen && cappingGreen

  const built = useMemo<BuiltValidationState | undefined>(() => {
    // Only build once a validation has actually run for this text; otherwise
    // there is nothing honest to persist.
    if (!serverVerdictIsCurrent) return undefined
    return buildValidationState({
      insight         : { templateString, templateCriteria : insight.templateCriteria },
      catalog,
      serverVariables : tokens ? { valid : tokens.valid, errors : tokens.errors } : undefined,
      // Absent when the model did not run — the honest encoding of a bypass.
      semantic        : semantic
        ? { verdict : semantic.verdict, reasoning : semantic.reasoning, issues : semantic.issues, model : semantic.model }
        : undefined,
      checkedAt       : new Date().toISOString(),
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serverVerdictIsCurrent, tokens, semantic, templateString, insight.templateCriteria, catalog])

  const gate = useMemo<ValidationGate>(() => ({
    requiresValidation   : textChanged,
    satisfied            : mechanicalGreen && (semanticGreen || canWaiveSemantic),
    semanticIsTheBlocker : mechanicalGreen && !semanticGreen,
    built,
  }), [textChanged, mechanicalGreen, semanticGreen, canWaiveSemantic, built])

  useEffect(() => { onGateChange?.(gate) }, [gate, onGateChange])


  // Kept for the caption and the detail alert, which name the offending
  // variables rather than only counting them.
  const criteriaCount = Object.keys(insight.templateCriteria ?? {}).length
  const missingInText = useMemo(
    () => criteriaVariablesMissingFromText(insight.templateCriteria, templateString, catalog),
    [insight.templateCriteria, templateString, catalog],
  )

  return (
    <Box sx={{ mt : 2 }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb : 1.5 }}>
        <Button
          variant="outlined"
          startIcon={isRunning ? <CircularProgress size={14} /> : <FactCheckIcon />}
          disabled={!templateString.trim() || isRunning || !textChanged}
          onClick={runValidation}
        >
          Validate
        </Button>
        <Typography variant="caption" color="text.secondary">
          {!templateString.trim()
            ? 'Write some template text to validate.'
            : !textChanged
              ? 'Template text is unchanged — already validated when it was saved.'
              : 'Checks tokens against the live registry, and meaning against the classification.'}
        </Typography>
      </Stack>

      <Stack direction="row" spacing={1.5}>
        <ValidationTile
          label="VARIABLES"
          state={variablesState}
          caption={tokenError ? 'Not checked' : variablesCaption}
          tooltip={
            serverVerdictIsCurrent
              ? 'Token syntax and DerivedQuantity resolvability, confirmed by the server. This is enforced on save.'
              : `${derivedVariables.detail ?? 'Checked locally against the catalog'} — run Validate to confirm against the server.`
          }
        />
        <ValidationTile
          label="SEMANTIC"
          state={semanticState}
          caption={
            semanticError ? 'Not checked'
            : semantic ? semantic.verdict
            // A verdict stored on a previous visit, not a fresh one.
            : storedSemantic.verdict === 'UNKNOWN' ? 'Not run' : storedSemantic.verdict
          }
          tooltip="Advisory LLM judgement on whether the wording matches the classification. Does not block saving."
        />
        <ValidationTile
          label="CAPPING"
          state={cappingState}
          caption={
            !criteriaCount ? 'No criteria'
            : missingInText.length ? `${missingInText.length} not in text`
            : 'Applied'
          }

          tooltip="Every variable used in template criteria must also appear as a token in the template text, or its capping applies to a value the sentence never renders."
        />
      </Stack>

      {!!definitionGaps.length && (
        <Alert severity="error" sx={{ mt : 1.5 }}>
          {definitionGaps.map((gap) => (
            <Box key={gap.code}>
              {displayNameFor(catalog, 'derivedQuantity', gap.code)} reads{' '}
              <strong>{gap.field}</strong>, which this insight does not set.
            </Box>
          ))}
          Fill the field in, or remove the variable — it resolves to nothing, and
          the token would reach users unreplaced.
        </Alert>
      )}

      {!!missingInText.length && (
        <Alert severity="error" sx={{ mt : 1.5 }}>
          {missingInText.map((code) => displayNameFor(catalog, 'derivedQuantity', code)).join(', ')}
          {missingInText.length === 1 ? ' is' : ' are'} constrained by a template
          criterion but never referenced in the template text. Insert the matching
          token, or remove the condition — otherwise the value is capped for a
          sentence that does not render it.
        </Alert>
      )}

      {tokenError && (
        <Alert severity="warning" sx={{ mt : 1.5 }}>
          {tokenError} Saving still applies the same rules server-side.
        </Alert>
      )}

      {serverVerdictIsCurrent && tokens && !tokens.valid && (
        <Alert severity="error" sx={{ mt : 1.5 }}>
          {tokens.errors.map((problem) => (
            <Typography key={problem} variant="caption" sx={{ display : 'block' }}>{problem}</Typography>
          ))}
        </Alert>
      )}

      {semanticError && (
        <Alert severity="info" sx={{ mt : 1.5 }}>
          {semanticError} This check is advisory and does not affect saving.
        </Alert>
      )}

      {semantic && (
        <Alert
          severity={semantic.verdict === 'PASS' ? 'success' : semantic.verdict === 'WARN' ? 'warning' : 'error'}
          sx={{ mt : 1.5 }}
        >
          <Typography variant="body2" sx={{ mb : semantic.issues.length ? 0.5 : 0 }}>
            {semantic.reasoning}
          </Typography>
          {semantic.issues.map((issue) => (
            <Typography key={issue} variant="caption" sx={{ display : 'block' }}>• {issue}</Typography>
          ))}
          {semantic.verdict !== 'PASS' && (
            <Typography variant="caption" sx={{ display : 'block', mt : 0.5, fontStyle : 'italic' }}>
              Advisory only — this does not prevent saving or publishing.
            </Typography>
          )}
        </Alert>
      )}
    </Box>
  )
}

export default TemplateValidationPanel
